import { Box3, Group, Matrix4, Quaternion, Vector3 } from 'three';
import type { Mesh, Object3D } from 'three';
import { FRONT_CORNERS, findWheelClusters, type MeshBox } from '../../model/wheelClustering';
import type { CarAsset, CarBounds, Vector3Tuple } from '../../types';

/**
 * 바퀴를 굴리기 위한 피벗 그룹.
 *
 * 바퀴 하나에 그룹을 **세 겹** 씁니다. 각 겹이 하나씩만 담당합니다.
 *   1. 허브 — 위치와 좌표축 정렬. 회전을 쓰지 않습니다.
 *   2. 조향 — Y축 회전만.
 *   3. 굴림 — Z축 회전만.
 *
 * 조향과 굴림을 나누는 이유는 한 그룹에 두 회전을 넣으면 오일러 순서에 따라 축이
 * 섞이기 때문입니다. 조향 안에 굴림을 두면 회전축이 조향에 따라 함께 돌아가서 실제
 * 차와 같아집니다.
 *
 * 정렬을 따로 떼는 이유는 실제로 겪은 버그입니다. three에서 `rotation`(오일러)과
 * `quaternion`은 서로를 갱신합니다. 정렬 쿼터니언을 넣은 그룹에 나중에 `rotation.y`를
 * 쓰면 그 값이 쿼터니언을 덮어써서 정렬이 사라집니다. 그러면 조향이 에셋의
 * rotationY만큼 틀어진 축으로 걸립니다 — 앞바퀴가 옆으로 눕는 모습이 됩니다.
 * (rotationY가 +π/2인 에셋에서는 부호가 우연히 맞아떨어져서 더 알아채기 어려웠습니다)
 */
export interface WheelPivots {
  /** 앞바퀴 두 개. Y축 회전으로 조향각을 받습니다. */
  steering: Group[];
  /** 네 바퀴 전부. Z축 회전으로 굴림을 받습니다. */
  rolling: Group[];
  /**
   * 코너별 서스펜션 피벗. Y 위치로 바퀴 상하 스트로크를 받습니다.
   *
   * 허브(정렬)와 조향 사이에 한 겹 더 두지 않고 허브 자체를 씁니다. 허브는 회전을
   * 쓰지 않는 그룹이라 위치만 움직여도 정렬 쿼터니언이 깨지지 않습니다.
   */
  suspension: Partial<Record<WheelCornerName, Group>>;
  /** 각 피벗의 기준 Y. 스트로크는 이 값에 더합니다. */
  suspensionRestY: Partial<Record<WheelCornerName, number>>;
}

/** wheelClustering이 쓰는 코너 이름입니다. */
export type WheelCornerName = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

/**
 * 씬마다 만들어 둔 피벗. 같은 씬에 두 번 만들지 않기 위한 것입니다.
 *
 * 이게 없으면 실제로 바퀴가 멈춥니다. React StrictMode(개발 모드)는 useMemo 팩토리를
 * **같은 렌더 안에서 두 번** 호출하고, 그중 **첫 번째** 반환값을 유지합니다.
 * 이 함수는 씬을 재부모화하므로 두 번째 호출이 첫 번째가 만든 피벗에서 바퀴를 다시
 * 빼앗아 갑니다. 그래서 유지되는 쪽(첫 번째)이 텅 빈 피벗이 되고, 회전을 걸어도
 * 아무 일도 일어나지 않습니다. 프로덕션 빌드에서는 한 번만 불려서 정상 동작하기 때문에
 * 개발 중에만 재현되는 종류였습니다.
 *
 * WeakMap이라 씬이 버려지면 함께 사라집니다.
 */
const pivotsByScene = new WeakMap<Object3D, WheelPivots | null>();

/** 앱 좌표계 변환: T(position) · Ry(rotationY) · S(scale). CarModel의 primitive와 같습니다. */
function createAssetMatrix(asset: CarAsset): Matrix4 {
  return new Matrix4().compose(
    new Vector3().fromArray(asset.position),
    new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), asset.rotationY),
    new Vector3(asset.scale, asset.scale, asset.scale)
  );
}

/**
 * carScene 하위 메시를 순회하며 **carScene 기준 상대 행렬**을 함께 넘깁니다.
 *
 * matrixWorld를 쓰지 않는 이유: 이 함수는 씬이 마운트되기 전(클론 직후)에 돌기도 하고
 * 그 뒤에 돌기도 합니다. matrixWorld는 R3F가 primitive에 넣는 변환이 반영됐는지에 따라
 * 달라져서, 호출 시점에 따라 결과가 바뀝니다. 부모부터 직접 곱해 올라가면 언제 불러도
 * 같은 값이 나옵니다.
 *
 * root 자신의 변환은 제외합니다. R3F가 매니페스트 값으로 덮어쓰기 때문에 원본 GLTF
 * 루트의 변환은 버려지고, 그 자리는 createAssetMatrix가 대신합니다.
 */
function forEachMeshRelativeToRoot(root: Object3D, visit: (mesh: Mesh, relative: Matrix4) => void): void {
  const walk = (node: Object3D, parentMatrix: Matrix4) => {
    node.updateMatrix();
    const matrix = new Matrix4().multiplyMatrices(parentMatrix, node.matrix);
    if ((node as Mesh).isMesh) {
      visit(node as Mesh, matrix);
    }
    node.children.forEach((child) => walk(child, matrix));
  };
  const identity = new Matrix4();
  root.children.forEach((child) => walk(child, identity));
}

/**
 * 씬에서 네 바퀴를 찾아 피벗 그룹으로 재부모화합니다.
 *
 * @returns 네 바퀴를 확신할 수 없으면 null. 그 경우 바퀴는 굴리지 않습니다 —
 *   억지로 돌리면 차체가 딸려 돌거나 한 바퀴만 구르는데, 둘 다 안 굴리는 것보다
 *   나쁩니다. 어떤 에셋이 왜 실패하는지는 model/wheelClustering 주석에 있습니다.
 *   (지금 카탈로그에서는 Model 3만 실패합니다. 지오메트리가 병합돼 있어 프리미티브가
 *   51개뿐이고 바퀴가 독립 형상으로 남아 있지 않습니다)
 */
export function createWheelPivots(carScene: Object3D, bounds: CarBounds, asset: CarAsset): WheelPivots | null {
  /*
   * 같은 씬에 두 번 만들지 않습니다. 두 번째 호출이 첫 번째의 바퀴를 빼앗아 가기
   * 때문입니다(위 pivotsByScene 주석). null도 캐시합니다 — 분리 불가한 에셋에서
   * 매번 전체 메시를 다시 훑을 이유가 없습니다.
   */
  const cached = pivotsByScene.get(carScene);
  if (cached !== undefined) {
    return cached;
  }

  const assetMatrix = createAssetMatrix(asset);
  const boxes: MeshBox[] = [];
  const meshesById = new Map<string, Mesh>();
  const scratch = new Box3();
  let index = 0;

  forEachMeshRelativeToRoot(carScene, (mesh, relative) => {
    const geometry = mesh.geometry;
    if (geometry.boundingBox === null) {
      geometry.computeBoundingBox();
    }
    if (geometry.boundingBox === null) {
      return;
    }
    // 앱 좌표계 AABB. 클러스터링이 쓰는 좌표계는 매니페스트 실측값과 같아야 합니다.
    scratch.copy(geometry.boundingBox).applyMatrix4(new Matrix4().multiplyMatrices(assetMatrix, relative));

    const id = `wheel-candidate-${index}`;
    index += 1;
    meshesById.set(id, mesh);
    boxes.push({
      id,
      min: [scratch.min.x, scratch.min.y, scratch.min.z],
      max: [scratch.max.x, scratch.max.y, scratch.max.z],
      /*
       * 삼각형 수. 인덱스가 있으면 그쪽이, 없으면 정점 수가 기준입니다.
       * 한 바퀴에서 가장 큰 형상(타이어)을 고르는 데만 쓰므로 정확한 값일 필요는 없고
       * 부품끼리 비교만 되면 됩니다.
       *
       * position이 없는 경우까지 0으로 받는 이유: 이 함수는 렌더 중에 돌기 때문에
       * 여기서 예외가 나면 ViewerErrorBoundary가 뷰어 전체를 에러 화면으로 바꿉니다.
       * 바퀴 하나를 못 찾는 것과 3D가 통째로 사라지는 것은 다른 문제입니다.
       */
      triangles: Math.round((geometry.index?.count ?? geometry.attributes.position?.count ?? 0) / 3),
    });
  });

  const clusters = findWheelClusters(boxes, bounds);
  if (clusters === null) {
    pivotsByScene.set(carScene, null);
    return null;
  }

  /*
   * 피벗의 로컬 축을 앱 좌표축에 맞춥니다.
   *
   * 피벗은 carScene의 자식이라 로컬 축이 **모델 공간** 축입니다. 그런데 모델은
   * rotationY(±π/2)로 돌려서 세우기 때문에, 모델 공간의 Z는 차량의 좌우축이 아닙니다
   * (Sorento는 부호까지 반대입니다). 회전을 반대로 걸어 두면 피벗 안에서는 X가 전후,
   * Z가 좌우가 되어, 조향은 Y·굴림은 Z로 어느 에셋에서나 같게 처리됩니다.
   */
  const alignToApp = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), -asset.rotationY);
  const inverseAsset = assetMatrix.clone().invert();

  const steering: Group[] = [];
  const rolling: Group[] = [];
  const suspension: Partial<Record<WheelCornerName, Group>> = {};
  const suspensionRestY: Partial<Record<WheelCornerName, number>> = {};

  clusters.forEach((cluster) => {
    // 회전 중심을 carScene 로컬 좌표로 되돌립니다(스케일까지 되돌려야 합니다).
    const hub = new Vector3().fromArray(cluster.center as Vector3Tuple).applyMatrix4(inverseAsset);

    /*
     * 허브: 위치와 좌표축 정렬만 담당합니다. 이 그룹의 회전은 앞으로 아무도 건드리지
     * 않습니다 — 건드리면 정렬이 지워집니다(위 WheelPivots 주석 참고).
     */
    const hubPivot = new Group();
    hubPivot.name = `wheel-hub-${cluster.corner}`;
    hubPivot.position.copy(hub);
    hubPivot.quaternion.copy(alignToApp);

    const steeringPivot = new Group();
    steeringPivot.name = `wheel-steering-${cluster.corner}`;

    const rollingPivot = new Group();
    rollingPivot.name = `wheel-rolling-${cluster.corner}`;

    /*
     * 순서가 중요합니다. attach는 대상의 matrixWorld를 써서 상대 변환을 계산하므로,
     * 피벗이 먼저 씬에 붙어 있어야 합니다. 반대로 하면 바퀴가 엉뚱한 자리로 갑니다.
     */
    steeringPivot.add(rollingPivot);
    hubPivot.add(steeringPivot);
    carScene.add(hubPivot);

    cluster.meshIds.forEach((id) => {
      const mesh = meshesById.get(id);
      if (mesh !== undefined) {
        // attach는 월드 변환을 유지하므로 재부모화만으로 겉모습이 바뀌지 않습니다.
        rollingPivot.attach(mesh);
      }
    });

    if (FRONT_CORNERS.includes(cluster.corner)) {
      steering.push(steeringPivot);
    }
    rolling.push(rollingPivot);
    // 허브는 위치 전용 그룹이라 서스펜션 스트로크를 여기에 넣습니다.
    suspension[cluster.corner as WheelCornerName] = hubPivot;
    suspensionRestY[cluster.corner as WheelCornerName] = hubPivot.position.y;
  });

  const pivots: WheelPivots = { steering, rolling, suspension, suspensionRestY };
  pivotsByScene.set(carScene, pivots);
  return pivots;
}

/** 주행을 끝낼 때 바퀴를 원래 각도로 되돌립니다. 조향각이 남으면 눈에 보입니다. */
export function resetWheelPivots(pivots: WheelPivots | null): void {
  if (pivots === null) {
    return;
  }
  pivots.steering.forEach((pivot) => {
    pivot.rotation.y = 0;
  });
  (Object.keys(pivots.suspension) as WheelCornerName[]).forEach((corner) => {
    const pivot = pivots.suspension[corner];
    const restY = pivots.suspensionRestY[corner];
    if (pivot !== undefined && restY !== undefined) {
      pivot.position.y = restY;
    }
  });
  pivots.rolling.forEach((pivot) => {
    pivot.rotation.z = 0;
  });
}
